// src/components/screens/AdvancedTreatmentsforHome/AutoScrollImageRow.jsx
import React, { useState, useEffect, useRef } from "react";
import "./AutoScrollImageRow.css";

// Import combined Before-After images
import img1 from "../../../assets/images/autoscroll1.jpg";
import img2 from "../../../assets/images/autoscroll2.jpg";
import img3 from "../../../assets/images/autoscroll3.jpg";
import img4 from "../../../assets/images/autoscroll4.jpg";
import img5 from "../../../assets/images/autoscroll5.jpg";
import img6 from "../../../assets/images/autoscroll6.jpg";
import img7 from "../../../assets/images/autoscroll7.jpg";
import img8 from "../../../assets/images/autoscroll8.jpg";
import img9 from "../../../assets/images/autoscroll9.jpg";
import img10 from "../../../assets/images/autoscroll10.jpg";

// Image metadata to display with each image
const imageData = [
  { id: 1, src: img1, title: "Treatment A", description: "4 weeks of treatment" },
  { id: 2, src: img2, title: "Treatment B", description: "6 weeks of treatment" },
  { id: 3, src: img3, title: "Treatment C", description: "8 weeks of treatment" },
  { id: 4, src: img4, title: "Treatment D", description: "12 weeks of treatment" },
  { id: 5, src: img5, title: "Treatment E", description: "4 weeks of treatment" },
  { id: 6, src: img6, title: "Treatment F", description: "6 weeks of treatment" },
  { id: 7, src: img7, title: "Treatment G", description: "8 weeks of treatment" },
  { id: 8, src: img8, title: "Treatment H", description: "12 weeks of treatment" },
  { id: 9, src: img9, title: "Treatment I", description: "8 weeks of treatment" },
  { id: 10, src: img10, title: "Treatment J", description: "12 weeks of treatment" },
];

const AutoScrollImageRow = () => {
  const [modalContent, setModalContent] = useState(null);
  const [isScrolling, setIsScrolling] = useState(true);
  const scrollTrackRef = useRef(null);
  const [scrollPosition, setScrollPosition] = useState(0);
  const clickedItemRef = useRef(null);

  // Pause scrolling when hovering over the container
  const handleMouseEnter = () => setIsScrolling(false);
  const handleMouseLeave = () => setIsScrolling(true);

  // Save current scroll position and clicked element when opening modal
  const openModal = (imageInfo, event) => {
    // Store the current scroll position
    if (scrollTrackRef.current) {
      setScrollPosition(scrollTrackRef.current.scrollLeft);
    }
    
    // Store reference to the clicked item
    if (event && event.currentTarget) {
      clickedItemRef.current = event.currentTarget;
    }
    
    setModalContent(imageInfo);
    document.body.style.overflow = 'hidden'; // Prevent background scrolling
  };

  // Restore scroll position when closing modal and focus back on clicked item
  const closeModal = () => {
    setModalContent(null);
    document.body.style.overflow = 'auto'; // Restore scrolling
    
    // Wait for the next render cycle to restore scroll position and focus
    setTimeout(() => {
      if (scrollTrackRef.current) {
        scrollTrackRef.current.scrollLeft = scrollPosition;
      }
      
      // Return focus to the clicked item
      if (clickedItemRef.current) {
        clickedItemRef.current.focus();
      }
    }, 10);
  };

  // Handle keyboard navigation in modal
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!modalContent) return;
      
      if (e.key === 'Escape') {
        closeModal();
      } else if (e.key === 'ArrowRight') {
        const currentIndex = imageData.findIndex(img => img.id === modalContent.id);
        const nextIndex = (currentIndex + 1) % imageData.length;
        setModalContent(imageData[nextIndex]);
      } else if (e.key === 'ArrowLeft') {
        const currentIndex = imageData.findIndex(img => img.id === modalContent.id);
        const prevIndex = (currentIndex - 1 + imageData.length) % imageData.length;
        setModalContent(imageData[prevIndex]);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [modalContent]);

  // Make the scroll track manually scrollable
  useEffect(() => {
    const trackElement = scrollTrackRef.current;
    if (!trackElement) return;

    // Store the current scroll position when user manually scrolls
    const handleScroll = () => {
      setScrollPosition(trackElement.scrollLeft);
    };

    trackElement.addEventListener('scroll', handleScroll);
    return () => trackElement.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="results-section">
      <h2 className="results-title">✅H-cura Results</h2>
      <p className="results-description">See the transformation our clients experienced with our treatment solutions</p>
      
      <div 
        className={`scroll-wrapper ${isScrolling ? 'scrolling' : 'paused'}`}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div className="scroll-track" ref={scrollTrackRef}>
          {[...imageData, ...imageData].map((img, index) => (
            <div 
              className="scroll-item" 
              key={index} 
              onClick={(e) => openModal(img, e)}
              tabIndex="0" // Make it focusable
            >
              <img
                src={img.src}
                alt={`Before & After Result: `}
                title="Click to view details"
              />
              <div className="image-label">
                {/* <span>{img.title}</span> */}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Modal with enhanced navigation and more visible close button */}
      {modalContent && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="close-button" onClick={closeModal}>×</button>
            
            <img src={modalContent.src} alt={`Before & After: ${modalContent.title}`} />
            
            {/* Treatment info moved below the image */}
            <div className="modal-info">
              <h3>{modalContent.title}</h3>
              <p>{modalContent.description}</p>
            </div>
            
            <div className="modal-navigation">
              <button 
                className="nav-button prev"
                onClick={() => {
                  const currentIndex = imageData.findIndex(img => img.id === modalContent.id);
                  const prevIndex = (currentIndex - 1 + imageData.length) % imageData.length;
                  setModalContent(imageData[prevIndex]);
                }}
              >
                &#10094;
              </button>
              <button 
                className="nav-button next"
                onClick={() => {
                  const currentIndex = imageData.findIndex(img => img.id === modalContent.id);
                  const nextIndex = (currentIndex + 1) % imageData.length;
                  setModalContent(imageData[nextIndex]);
                }}
              >
                &#10095;
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AutoScrollImageRow;
